import React from 'react';
import {
  SimpleForm,
  SelectInput,
  TextInput,
  NumberInput,
  number,
  minValue,
  maxValue,
  required,
  useGetList,
  useInput,
} from 'react-admin';

import type { FacetGroup } from '@src/facets';
import type { ConditionOption } from '@src/conditions';

interface ConditionDiscountsFormProps {
  toolbar?: React.ReactElement | false;
}

/**
 * Form fields for primary name that depend on the selected primary tag.
 */
const PrimaryNameInput = () => {
  const { field: tagField } = useInput({ source: 'tag' });
  const selectedTag = tagField.value as string;
  const { data: facetGroups = [] } = useGetList<FacetGroup>('facets');

  const tagFacet = facetGroups.find(fg => fg.tag === selectedTag);
  const nameChoices = tagFacet?.values.map(v => ({
    id: v.name,
    name: v.name,
  })) || [];

  return (
    <SelectInput
      source="name"
      label="Primary Name"
      choices={nameChoices}
      disabled={!selectedTag || nameChoices.length === 0}
      fullWidth
      resettable
      helperText={!selectedTag ? 'Select a primary tag first' : undefined}
    />
  );
};

const ConditionDiscountsForm = ({ toolbar }: ConditionDiscountsFormProps) => {
  const { data: facetGroups = [], isLoading: facetsLoading } = useGetList<FacetGroup>('facets');
  const { data: conditions = [], isLoading: conditionsLoading } = useGetList<ConditionOption>('conditions');

  const conditionChoices = conditions.map(c => ({ id: c.code, name: c.label }));

  const departmentFacet = facetGroups.find(fg => fg.tag === 'department');
  const categoryFacet = facetGroups.find(fg => fg.tag === 'category');

  const departmentChoices = departmentFacet?.values.map(v => ({ id: v.name, name: v.name })) || [];
  const categoryChoices = categoryFacet?.values.map(v => ({ id: v.name, name: v.name })) || [];
  const tagChoices = facetGroups.map(fg => ({ id: fg.tag, name: fg.tag }));

  return (
    <SimpleForm toolbar={toolbar}>
      <SelectInput
        source="condition"
        label="Condition"
        choices={conditionChoices}
        disabled={conditionsLoading}
        fullWidth
        helperText="Leave empty for all conditions (catch-all rule)"
        resettable
      />
      <NumberInput
        source="discount"
        label="Discount (%)"
        validate={[required(), number(), minValue(0), maxValue(100)]}
        fullWidth
        helperText="Percentage of the item's price: 0-100."
      />
      <SelectInput
        source="department"
        label="Department"
        choices={departmentChoices}
        disabled={facetsLoading}
        fullWidth
        resettable
        helperText="Leave empty for all departments"
      />
      <SelectInput
        source="category"
        label="Category"
        choices={categoryChoices}
        disabled={facetsLoading}
        fullWidth
        resettable
        helperText="Leave empty for all categories"
      />
      <TextInput
        source="merchantCategory"
        label="Merchant Category"
        fullWidth
        helperText="Leave empty for all merchant categories"
      />
      <SelectInput
        source="tag"
        label="Tag"
        choices={tagChoices}
        disabled={facetsLoading}
        fullWidth
        resettable
        helperText="Leave empty for all tags"
      />
      <PrimaryNameInput />
    </SimpleForm>
  );
};

export default ConditionDiscountsForm;
